import { useMemo, useState } from 'react';
import { tw } from '@twind/core';
import { DidomiButton, DidomiButtonGroup, DidomiCardContainer, DidomiSelect, DidomiSelectOption, DidomiSelectOptions } from '@didomi/ui-atoms-react';
import { selectOrganization } from './api';
import { DidomiLogo } from './components/DidomiLogo';
import { ErrorView } from './components/ErrorView';
import { PermissionsDisplay } from './components/PermissionsDisplay';
import { showConnectedSnackbar, showConnectingSnackbar } from './connectSnackbar';
import { SHOW_OAUTH_DEBUG_PANEL } from './constants';
import { readOAuthContext } from './readContext';
import type { ValidatedOAuthContext } from './types';
import { formatDebug, postToOpener } from './utils/helpers';
import { validateSuccessContext } from './utils/validation';
import './org-selector-button-group.scss';

function SuccessView({
  ctx,
  onConnectionError,
}: {
  ctx: ValidatedOAuthContext;
  /** Set a message to open the connection-error modal, or null to clear. */
  onConnectionError: (message: string | null) => void;
}) {
  const [organizationId, setOrganizationId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = !submitting && typeof ctx.sessionId === 'string' && !!ctx.sessionId && !!organizationId;

  const title = `Connect ${ctx.integration_name}`;

  const handleAccept = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    onConnectionError(null);

    let closeConnecting: (() => void) | undefined;
    try {
      closeConnecting = await showConnectingSnackbar();

      const result = await selectOrganization({
        sessionId: ctx.sessionId!,
        organizationId,
      });

      const code = (result as any)?.code;
      const state = (result as any)?.state;
      const redirectTo = (result as any)?.redirect_to;

      if (typeof redirectTo === 'string' && redirectTo.trim()) {
        closeConnecting();
        window.location.assign(redirectTo);
        return;
      }

      if (typeof code === 'string' && code && typeof state === 'string' && state) {
        postToOpener({ code, state, organizationId });
        // Leave submitting true until close so the connect button cannot fire again.
        // Do not call closeConnecting() here: @didomi/utility replaces an open snackbar when
        // displaySnackbar runs; closing first causes removeChild errors and hides the next toast.
        await showConnectedSnackbar();
        setTimeout(() => window.close(), 600);
        return;
      }

      throw new Error(`Unexpected response from /oauth/select-organization: ${JSON.stringify(result)}`);
    } catch (e: any) {
      closeConnecting?.();
      onConnectionError(e?.message || 'Failed to connect');
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (submitting) return;
    postToOpener({ cancelled: true });
    window.close();
  };

  return (
    <div className={tw('flex min-h-screen w-full justify-center bg-neutral-gray-2 p-3')}>
      <DidomiCardContainer
        className={tw('mx-auto max-w-[692px] w-full h-max')}
        style={{
          '--card-inner-padding': '16px',
        }}
      >
        <div className={tw('flex flex-col items-center gap-3 text-center')}>
          <DidomiLogo />
          <h1 className={tw('heading-3 mb-4 text-neutral-gray-8')}>{title}</h1>
        </div>

        <PermissionsDisplay ctx={ctx} />

        <DidomiCardContainer
          style={{
            '--card-inner-bg': '#F9FAFA',
            '--card-inner-padding': '16px',
          }}
        >
          <p className={tw('body-big !font-semibold m-0 mb-3 text-center text-neutral-gray-400')}>Select which Didomi organization to connect to this store</p>
          <DidomiCardContainer
            style={{
              '--card-inner-bg': '#FFFFFF',
              '--card-inner-padding': '16px',
            }}
          >
            <DidomiSelect
              id="organization"
              label="Didomi organization *"
              value={organizationId}
              disabled={submitting}
              onValueChange={(event: any) => {
                const value = event?.detail ?? event?.target?.value ?? event?.value ?? '';
                const normalized = Array.isArray(value) ? String(value[0] ?? '') : String(value ?? '');
                setOrganizationId(normalized);
              }}
              placeholder="Select an organization"
            >
              <DidomiSelectOptions>
                {ctx.organizations.map(org => {
                  return <DidomiSelectOption key={org.id} value={org.id} label={org.name} />;
                })}
              </DidomiSelectOptions>
            </DidomiSelect>

            <DidomiButtonGroup className={tw('org-selector-button-group flex w-full flex-nowrap')}>
              <DidomiButton variant="secondary" onClick={handleCancel} disabled={submitting}>
                Cancel
              </DidomiButton>
              <DidomiButton iconLeft="link" onClick={handleAccept} disabled={!canSubmit} fullWidth>
                Connect this organization
              </DidomiButton>
            </DidomiButtonGroup>
          </DidomiCardContainer>
        </DidomiCardContainer>

        {SHOW_OAUTH_DEBUG_PANEL ? (
          <details className={tw('body-small text-neutral-gray-6')}>
            <summary className={tw('cursor-pointer font-bold')}>Debug Info (click to expand)</summary>
            <pre className={tw('mt-xs whitespace-pre-wrap break-words')}>{formatDebug(ctx)}</pre>
          </details>
        ) : null}
      </DidomiCardContainer>
    </div>
  );
}

export function OrgSelectorApp() {
  const ctx = useMemo(() => readOAuthContext(), []);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  if (ctx.error) {
    return (
      <div className={tw('box-border min-h-screen w-full bg-[linear-gradient(180deg,#fafafa_0%,#f2e9f3_55%,#ede5ee_100%)]')}>
        <ErrorView ctx={ctx} />
      </div>
    );
  }

  validateSuccessContext(ctx);

  return (
    <>
      <SuccessView ctx={ctx} onConnectionError={setConnectionError} />
      {connectionError ? <ErrorView ctx={{ error: 'Connection failed', error_description: connectionError }} onClose={() => setConnectionError(null)} /> : null}
    </>
  );
}
